import React, { useState, useEffect } from "react";
import Card from "../../components/Card";
import Action from "../../components/Action";
import Heading from "../../components/Heading";
import RadioQuestion from "../../components/RadioQuestion";
import TextQuestion from "../../components/TextQuestion";
import CountryPicker from "../../components/CountryPicker";
import PostcodeLookup from "../../components/PostcodeLookup";
import ScrollToTop from "../../components/utils/ScrollToTop";
import ErrorMessage from "../../components/ErrorMessage";
import { YES_NO_OPTIONS } from "../../constants/index";
import Image from "../../components/Image";
import imageCatAndHousePlant from "../../images/cat-and-houseplant.png";
import { isPhoneNumberValid } from "../../shared/utils/numberUtils";
import { isEmailValid } from "../../shared/utils/validation";

const ContactDetails = ({
  contactDetails,
  sellingAddressDetails,
  transactionType,
  clientIsCompany,
  onStepComplete,
}) => {
  // Initialize state
  const [emailAddress, setEmailAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [clientAddressIsSaleAddress, setClientAddressIsSaleAddress] = useState(null);
  const [clientAddress, setClientAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    addressTown: "",
    addressCounty: "",
    addressPostcode: "",
    addressCountryCode: "GBR",
  });
  const [errors, setErrors] = useState({});
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  // Error messages for each field
  const errorMessages = {
    emailAddress: "Please enter a valid email address",
    phoneNumber: "Please enter a valid phone number",
    clientAddressIsSaleAddress: "Please choose an option",
    addressLine1: "Please enter the first line of your address",
    addressTown: "Please enter your town",
    addressPostcode: "Please enter your postcode",
    addressCountryCode: "Please select a country",
  };

  // Initialize state from props if contactDetails exist
  useEffect(() => {
    if (contactDetails) {
      setEmailAddress(contactDetails.emailAddress || "");
      setPhoneNumber(contactDetails.phoneNumber || "");
      setClientAddressIsSaleAddress(contactDetails.clientAddressIsSaleAddress);
      setClientAddress({ ...contactDetails.clientAddress });
    }
  }, [contactDetails]);

  const handleValidation = () => {
    let errors = {};
    let formIsValid = true;

    // Email validation
    if (!emailAddress || !isEmailValid(emailAddress)) {
      errors["emailAddress"] = true;
      formIsValid = false;
    }

    // Phone number validation
    if (!phoneNumber || !isPhoneNumberValid(phoneNumber)) {
      errors["phoneNumber"] = true;
      formIsValid = false;
    }

    // Client address is sale address validation (for selling transactions)
    if (transactionType === "Selling" || transactionType === "BuyingAndSelling") {
      if (clientAddressIsSaleAddress == null) {
        errors["clientAddressIsSaleAddress"] = true;
        formIsValid = false;
      }
    }

    // Address validation
    if (!clientAddress.addressLine1) {
      errors["addressLine1"] = true;
      formIsValid = false;
    }

    if (!clientAddress.addressTown) {
      errors["addressTown"] = true;
      formIsValid = false;
    }

    if (!clientAddress.addressPostcode) {
      errors["addressPostcode"] = true;
      formIsValid = false;
    }

    if (!clientAddress.addressCountryCode) {
      errors["addressCountryCode"] = true;
      formIsValid = false;
    }

    setErrors(errors);
    setHasValidationErrors(!formIsValid);
    return formIsValid;
  };

  const handleFieldValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    if (!isBlur) {
      setHasValidationErrors(false);
    }
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = null;
      } else {
        // Field-specific validation on blur
        if (field === "emailAddress") {
          if (!emailAddress || !isEmailValid(emailAddress)) {
            newErrors[field] = true;
          } else {
            newErrors[field] = null;
          }
        } else if (field === "phoneNumber") {
          if (!phoneNumber || !isPhoneNumberValid(phoneNumber)) {
            newErrors[field] = true;
          } else {
            newErrors[field] = null;
          }
        } else if (field === "clientAddressIsSaleAddress") {
          if (clientAddressIsSaleAddress == null) {
            newErrors[field] = true;
          } else {
            newErrors[field] = null;
          }
        } else {
          // For other fields, just check if they're empty
          const fieldValue = field.startsWith("address") ? clientAddress[field] : emailAddress;
          if (!fieldValue) {
            newErrors[field] = true;
          } else {
            newErrors[field] = null;
          }
        }
      }

      return newErrors;
    });
  };

  const handleNestedFieldValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    if (!isBlur) {
      setHasValidationErrors(false);
    }
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = null;
      } else {
        if (!clientAddress[field]) {
          newErrors[field] = true;
        } else {
          newErrors[field] = null;
        }
      }

      return newErrors;
    });
  };

  const handleAddressChange = (field) => (event) => {
    setClientAddress((prev) => ({
      ...prev,
      [field]: event,
    }));
  };

  const addressSelected = (address) => {
    setClientAddress((prev) => ({ ...prev, ...address, addressCountryCode: "GBR" }));
    setErrors((prev) => ({ ...prev, addressLine1: null, addressTown: null, addressPostcode: null }));
  };

  const saveContactDetails = (event) => {
    event.preventDefault();

    if (handleValidation()) {
      // This step decides the sale address but not the price, which the quote may have prefilled.
      const price = sellingAddressDetails?.price;
      if (clientAddressIsSaleAddress === true) {
        const { addressCountryCode, ...addressWithoutCountry } = clientAddress;
        onStepComplete({
          contactDetails: {
            emailAddress,
            phoneNumber,
            clientAddress,
            clientAddressIsSaleAddress,
          },
          sellingAddressDetails: {
            price,
            propertyAddress: addressWithoutCountry,
          },
        });
      } else {
        onStepComplete({
          contactDetails: {
            emailAddress,
            phoneNumber,
            clientAddress,
            clientAddressIsSaleAddress,
          },
          sellingAddressDetails: price == null ? null : { price },
        });
      }
    }
  };

  return (
    <React.Fragment>
      <ScrollToTop />
      <Card title="Tell us your contact details" headingSize="2" removeTopPadding>
        <TextQuestion
          question="What's your email address?"
          inputMode="email"
          autoComplete="email"
          value={emailAddress}
          valueEntered={setEmailAddress}
          validate={handleFieldValidation("emailAddress")}
          validationError={errors.emailAddress ? errorMessages.emailAddress : null}
        />

        <TextQuestion
          question="And your phone number?"
          type="tel"
          autoComplete="tel"
          value={phoneNumber}
          valueEntered={setPhoneNumber}
          validate={handleFieldValidation("phoneNumber")}
          validationError={errors.phoneNumber ? errorMessages.phoneNumber : null}
        />

        <Heading level="3" size="3">
          What's your postal address?
        </Heading>

        {clientIsCompany && (
          <Heading level="4" size="4">
            We'll need this for our identity checks
          </Heading>
        )}

        <PostcodeLookup address={clientAddress} errors={errors} onAddressSelected={addressSelected}>
          <TextQuestion
            question="Address line 1"
            autoComplete="address-line1"
            value={clientAddress.addressLine1 || ""}
            valueEntered={handleAddressChange("addressLine1")}
            validate={handleNestedFieldValidation("addressLine1")}
            validationError={errors.addressLine1 ? errorMessages.addressLine1 : null}
          />

          <TextQuestion
            question="Address line 2"
            optional={true}
            autoComplete="address-line2"
            value={clientAddress.addressLine2 || ""}
            valueEntered={handleAddressChange("addressLine2")}
          />

          <TextQuestion
            question="Address line 3"
            optional={true}
            autoComplete="address-line3"
            value={clientAddress.addressLine3 || ""}
            valueEntered={handleAddressChange("addressLine3")}
          />

          <TextQuestion
            question="Town"
            autoComplete="address-level2"
            value={clientAddress.addressTown || ""}
            valueEntered={handleAddressChange("addressTown")}
            validate={handleNestedFieldValidation("addressTown")}
            validationError={errors.addressTown ? errorMessages.addressTown : null}
          />

          <TextQuestion
            question="County"
            optional={true}
            value={clientAddress.addressCounty || ""}
            valueEntered={handleAddressChange("addressCounty")}
          />

          <TextQuestion
            question="Postcode"
            size="small"
            autoComplete="postal-code"
            value={clientAddress.addressPostcode || ""}
            valueEntered={handleAddressChange("addressPostcode")}
            validate={handleNestedFieldValidation("addressPostcode")}
            validationError={errors.addressPostcode ? errorMessages.addressPostcode : null}
          />
        </PostcodeLookup>

        <CountryPicker
          question="Country"
          value={clientAddress.addressCountryCode || ""}
          onChange={handleAddressChange("addressCountryCode")}
          validate={handleNestedFieldValidation("addressCountryCode")}
          validationError={errors.addressCountryCode ? errorMessages.addressCountryCode : null}
          placeholder="Start typing a country name..."
        />

        {(transactionType === "Selling" || transactionType === "BuyingAndSelling") && (
          <RadioQuestion
            question="Is this the address of the property you are selling?"
            options={YES_NO_OPTIONS}
            inline={true}
            name="clientAddressIsSaleAddress"
            selected={clientAddressIsSaleAddress}
            optionSelected={setClientAddressIsSaleAddress}
            validate={handleFieldValidation("clientAddressIsSaleAddress")}
            validationError={errors["clientAddressIsSaleAddress"] ? errorMessages.clientAddressIsSaleAddress : null}
          />
        )}

        {hasValidationErrors && (
          <ErrorMessage>Please make sure all mandatory fields are completed correctly before continuing.</ErrorMessage>
        )}

        <Action onClick={saveContactDetails}>Continue</Action>
      </Card>
      <Image src={imageCatAndHousePlant} />
    </React.Fragment>
  );
};

export default ContactDetails;
